import React, { useEffect, useState, useCallback } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import moment from "moment";
import "moment/locale/fr";
import { api, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useStatuts } from "@/context/StatutsContext";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import VisiteFormModal from "@/components/VisiteFormModal";

moment.locale("fr");
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const messages = {
  today: "Aujourd'hui", previous: "Précédent", next: "Suivant",
  month: "Mois", week: "Semaine", day: "Jour", agenda: "Agenda",
  date: "Date", time: "Heure", event: "Rendez-vous", noEventsInRange: "Aucun rendez-vous sur cette période.",
  showMore: (n) => `+${n} autres`,
};

export default function Planification() {
  const { user } = useAuth();
  const { statuts } = useStatuts();
  const navigate = useNavigate();
  const isTech = user.role === "technicien";
  const isPartner = user.role === "installateur";
  const canCreate = ["admin", "planification", "installateur"].includes(user.role);
  const canManage = ["admin", "planification"].includes(user.role);
  const [events, setEvents] = useState([]);
  const [techs, setTechs] = useState([]);
  const [filters, setFilters] = useState({ technicien_id: "", statut: "", code_postal: "" });
  const [view, setView] = useState(Views.MONTH);
  const [date, setDate] = useState(new Date());
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.technicien_id) params.set("technicien_id", filters.technicien_id);
    if (filters.statut) params.set("statut", filters.statut);
    if (filters.code_postal) params.set("code_postal", filters.code_postal);
    const { data } = await api.get(`/visites/calendar?${params}`);
    const evs = data.filter((v) => v.date_visite).map((v) => {
      const start = new Date(`${v.date_visite}T${v.heure_debut || "09:00"}:00`);
      const end = new Date(`${v.date_visite}T${v.heure_fin || v.heure_debut || "10:00"}:00`);
      const title = v.masked ? `${v.heure_debut || ""} — ${v.code_postal}` : `${v.heure_debut || ""} ${v.code_postal} - ${v.client_nom}`;
      return { id: v.id, title, start, end: end > start ? end : new Date(start.getTime() + 3600000), color: v.statut_color, masked: v.masked, resource: v };
    });
    setEvents(evs);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canManage) api.get("/users/techniciens").then((r) => setTechs(r.data)).catch(() => {}); }, [canManage]);

  const moveEvent = async ({ event, start, end, resourceId }) => {
    if (event.masked) return;
    const patch = { date_visite: moment(start).format("YYYY-MM-DD"), heure_debut: moment(start).format("HH:mm"), heure_fin: moment(end).format("HH:mm") };
    if (resourceId && resourceId !== event.resourceId) {
      if (resourceId === "__none__") { toast.info("Déposez sur une colonne technicien pour affecter le rendez-vous"); }
      else { patch.technicien_id = resourceId; }
    }
    try { await api.patch(`/visites/${event.id}/planning`, patch); toast.success(patch.technicien_id ? "Rendez-vous déplacé et réaffecté" : "Rendez-vous déplacé"); load(); }
    catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(apiError(e) + "\n\nForcer le déplacement ?")) {
          try { await api.patch(`/visites/${event.id}/planning`, { ...patch, force: true }); toast.success("Déplacé (forcé)"); load(); }
          catch (e2) { toast.error(apiError(e2)); }
        } else load();
      } else { toast.error(apiError(e)); load(); }
    }
  };

  const eventStyleGetter = (event) => ({ style: { backgroundColor: event.masked ? "#cbd5e1" : event.color, color: event.masked ? "#475569" : "#fff", opacity: event.masked ? 0.85 : 1 } });

  const setPeriod = (v) => { setView(v); setDate(new Date()); };

  const dayByTech = canManage && view === Views.DAY;
  const resources = dayByTech
    ? [{ resourceId: "__none__", resourceTitle: "Non affecté" },
       ...techs.map((t) => ({ resourceId: t.id, resourceTitle: `${t.prenom} ${t.nom}` }))]
    : undefined;
  const dayEvents = dayByTech ? events.map((e) => ({ ...e, resourceId: e.resource?.technicien_id || "__none__" })) : events;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{isTech ? "Mon planning" : "Planification"}</h1>
          <p className="text-sm text-slate-500">
            {isTech ? "Vos rendez-vous à venir." : isPartner ? "Planning global — les rendez-vous des autres partenaires sont masqués (code postal uniquement)." : "Glissez-déposez pour replanifier · les conflits sont détectés automatiquement."}
          </p>
        </div>
        {canCreate && (
          <button data-testid="planif-add-rdv" onClick={() => setShowForm(true)} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-slate-800">
            <Plus className="h-4 w-4" /> Ajouter un rendez-vous
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex rounded-lg border border-slate-200 p-1">
          {[[Views.DAY, "Aujourd'hui / Jour"], [Views.WEEK, "Semaine"], [Views.MONTH, "Mois"]].map(([v, l]) => (
            <button key={v} data-testid={`period-${v}`} onClick={() => setPeriod(v)} className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${view === v ? "bg-[#0A0A0A] text-white" : "text-slate-500 hover:text-slate-900"}`}>{l}</button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="planif-cp-search"
            value={filters.code_postal}
            onChange={(e) => setFilters({ ...filters, code_postal: e.target.value.replace(/[^0-9]/g, "") })}
            placeholder="Code postal (ex : 69, 690, 69008)"
            className="w-64 rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-slate-400"
          />
        </div>
        {canManage && (
          <>
            <select data-testid="cal-filter-tech" value={filters.technicien_id} onChange={(e) => setFilters({ ...filters, technicien_id: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">Tous les techniciens</option>
              {techs.map((t) => <option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>)}
            </select>
            <select data-testid="cal-filter-statut" value={filters.statut} onChange={(e) => setFilters({ ...filters, statut: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">Tous statuts</option>
              {statuts.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
          </>
        )}
        <span className="ml-auto text-xs text-slate-400">{events.length} rendez-vous affichés</span>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4" style={{ height: "70vh" }} data-testid="calendar">
        <DnDCalendar
          localizer={localizer}
          events={dayEvents}
          messages={messages}
          culture="fr"
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          startAccessor="start"
          endAccessor="end"
          popup
          resources={resources}
          resourceIdAccessor="resourceId"
          resourceTitleAccessor="resourceTitle"
          draggableAccessor={(e) => canManage && !e.masked}
          resizable={canManage}
          onEventDrop={moveEvent}
          onEventResize={moveEvent}
          onSelectEvent={(e) => { if (!e.masked) navigate(`/visites/${e.id}`); }}
          eventPropGetter={eventStyleGetter}
          style={{ height: "100%" }}
        />
      </div>

      {showForm && <VisiteFormModal mode="create" onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); toast.success("Rendez-vous créé"); }} />}
    </div>
  );
}
